import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import { useDispatch, useSelector } from 'react-redux'
import { updateFilter, showFilter } from '../../../../../store/actions/filter.actions'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import { updateSkip } from '../../../../../store/actions/datatable.actions'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import InputField from '../../../../FormControl/input/InputField'
import InputDateUTC from '../../../../FormControl/input/InputDateUTC'
import { Typography } from '@mui/material'
import { SelectMultipleFieldYears } from '../../../../FormControl/SelectRequest/SelectMultipleFieldYears'
import SelectField from '../../../../FormControl/input/SelectField'
import { handlerErrorV2 } from '../../../../../utils/errors'
import FormCurriculumElements from './FormCurriculumElements'
import { getSubjects } from '../../../../../services/subjects/subjects.service'

const FilterDialog = ({ module }) => {
  const { curriculumId } = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const years = useSelector((state) => state?.tenant?.years || [])
  const filter = useSelector((state) => state?.filter?.[module]?.filter || {})
  const open = useSelector((state) => state?.filter?.[module]?.open || false)

  const dispatch = useDispatch()

  const [form, setInputForm] = useState(filter)
  const [inputErrors, setInputErrors] = useState({})
  const [subjects, setSubjects] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isEmpty(filter)) {
      setInputForm(filter)
    }
  }, [filter])

  const onClose = () => {
    dispatch(showFilter(module, false))
  }
  const onChange = (key, value = '') => {
    if (key === 'yearLevel') {
      const yearLevel_name = years
        .filter((year) => value.includes(year._id))
        .map((year) => ({ _id: year._id, name: year.name }))
      setInputForm({ ...form, [key]: value, yearLevel_name })
    } else if (key === 'subjectId') {
      const find = subjects
        .filter((item) => item._id === value)
        .map((item) => ({ _id: item._id, name: item.name }))
      setInputForm({ ...form, [key]: value, subjectId_name: find })
    } else {
      setInputForm({ ...form, [key]: value })
    }
  }

  const onApply = () => {
    onClose()
    const filter = {
      ...form
    }
    dispatch(updateFilter(module, filter))
    dispatch(updateSkip(module, 0))
    navigate(pathname, { state: filter })
  }

  const onLoad = useCallback(async () => {
    try {
      const filter = {
        curriculumelements: form.curriculumElements
      }
      if (!isEmpty(filter)) {
        setIsLoading(true)
        const response = await getSubjects(curriculumId, filter)
        if (!response?.data && response?.error) {
          const message = response.error.message || 'An error ocurred'
          setInputErrors({ message })
        } else {
          setSubjects(response.data)
          const find = response.data.find((item) => item._id === form.subjectId)
          if (!find) {
            onChange('subjectId', '')
          }
        }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      setInputErrors({ message })
    } finally {
      setIsLoading(false)
    }
  }, [curriculumId, form.curriculumElements])

  useEffect(() => {
    onLoad()
  }, [onLoad])

  return (
    <DialogConfirmCancel
      id='dr-filter'
      open={open}
      title='Filter'
      maxWidth='xs'
      onCancel={onClose}
      onConfirm={onApply}
      textConfirm='Apply'
    >
      <Box className='grid grid-cols-4 gap-4'>
        {['dr', 'ul'].includes(module) && (
          <>
            <Typography
              variant='inherit'
              className='font-bold capitalize'
              color='primary'
              align='right'
              component='p'
            >
              Name
            </Typography>
            <div className='col-span-3'>
              <InputField
                id='name'
                name='name'
                value={form.name}
                onChange={(e) => onChange('name', e.target.value)}
              />
            </div>
          </>
        )}
        <Typography
          variant='inherit'
          className='font-bold capitalize'
          color='primary'
          align='right'
          component='p'
        >
          Year Level
        </Typography>
        <div className='col-span-3'>
          <SelectMultipleFieldYears
            id='yearLevel'
            name='yearLevel'
            value={form.yearLevel}
            onChange={(e) => onChange('yearLevel', e.target.value)}
          />
        </div>
        <FormCurriculumElements formData={form?.curriculumElements || {}} onChangeAlert={onChange} />
        <Typography
          variant='inherit'
          className='font-bold capitalize'
          color='primary'
          align='right'
          component='p'
        >
          Subject
        </Typography>
        <div className='col-span-3'>
          <SelectField
            id='subjectId'
            name='subjectId'
            value={form.subjectId || ''}
            onChange={(e) => onChange('subjectId', e.target.value)}
            options={
              subjects?.map((item) => ({
                value: item._id,
                label: item.name
              })) || []
            }
            placeholder='Choose an option'
            loading={isLoading}
          />
        </div>
        <Typography
          variant='inherit'
          className='font-bold capitalize'
          color='primary'
          align='right'
          component='p'
        >
          Period
        </Typography>
        <div className='col-span-3 grid grid-cols-2 items-center gap-4'>
          <InputDateUTC
            id='dr-start-date'
            name='startDate'
            value={module === 'dr' ? form?.periodStartDate : form?.startDate}
            maxDate={module === 'dr' ? form?.periodEndDate : form?.endDate}
            placeholder={'Start Date'}
            onChange={(value) => onChange(module === 'dr' ? 'periodStartDate' : 'startDate', value)}
          />
          <InputDateUTC
            id='dr-end-date'
            name='endDate'
            value={module === 'dr' ? form?.periodEndDate : form?.endDate}
            minDate={module === 'dr' ? form?.periodStartDate : form?.startDate}
            placeholder={'End Date'}
            isStartOfTheDay={false}
            onChange={(value) => onChange(module === 'dr' ? 'periodEndDate' : 'endDate', value)}
          />
        </div>
        <Typography
          variant='inherit'
          className='font-bold capitalize'
          color='primary'
          align='right'
          component='p'
        >
          Is Active
        </Typography>
        <div className='col-span-3'>
          <SelectField
            id='dr-is-actived'
            name='isActived'
            value={form.isActived}
            placeholder={'Choose an option'}
            onChange={(e) => onChange('isActived', e.target.value)}
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' }
            ]}
          />
        </div>
        {!['ul'].includes(module) && (
          <>
            <Typography
              variant='inherit'
              className='font-bold capitalize'
              color='primary'
              align='right'
              component='p'
            >
              Is linked
            </Typography>
            <div className='col-span-3'>
              <SelectField
                id='dr-is-linked-to-class'
                name='isLinked'
                value={form.isLinked}
                placeholder={'Choose an option'}
                onChange={(e) => onChange('isLinked', e.target.value)}
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' }
                ]}
              />
            </div>
          </>
        )}
      </Box>
    </DialogConfirmCancel>
  )
}

export default FilterDialog
